import React, { lazy, Suspense, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import Login, { isAuthFlowRoute } from './pages/Login';
import LegacyLogin from './pages/LegacyLogin';
import LoggedIn from './pages/LoggedIn';
import AuthCallback from './pages/AuthCallback';
import AuthDebug from './pages/AuthDebug';
import { acceptPendingInvitation, authErrorKey, bootstrapAuthSession, loadAuthConfig } from './utils/loginUtils';
import { PreferencesProvider } from './context/PreferencesContext';
import './i18n';
import { saveBillingCurrency } from './utils/billingCatalog';
import { NoteProvider } from './context/NoteContext';
import { logout } from './utils/logoutUtils';

// Keep registration UI, styling and requests out of returning-user sessions.
const ProfileOnboarding = lazy(() => import('./components/ProfileOnboarding'));

function App() {
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [config, setConfig] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const pathname = window.location.pathname;
  const isCallback = pathname === '/auth-callback';
  const isDebug = pathname === '/auth-debug';
  const isFlow = isAuthFlowRoute(pathname);

  useEffect(() => {
    let cancelled = false;
    setError(''); setReady(false);
    (async () => {
      const currentConfig = await loadAuthConfig();
      if (cancelled) return;
      setConfig(currentConfig);
      const currency = new URLSearchParams(window.location.search).get('currency');
      if (['gbp', 'eur'].includes(currency)) saveBillingCurrency(currency);
      if (!isCallback && (!isFlow || !currentConfig.enabled) && !isDebug) {
        const session = await bootstrapAuthSession(currentConfig);
        if (cancelled) return;
        const authenticated = session.isAuthenticated === true;
        if (authenticated && currentConfig.enabled) await acceptPendingInvitation();
        if (cancelled) return;
        setIsLoggedIn(authenticated);
        setNeedsOnboarding(authenticated && session.needs_onboarding === true);
      }
      setReady(true);
    })().catch(err => { if (!cancelled) setError(err?.code ? authErrorKey(err) : 'authModern.errors.unavailable'); });
    return () => { cancelled = true; };
  }, [isCallback, isFlow, isDebug, retry]);

  if (error) return <main className="auth-loading"><p role="alert">{t(error)}</p>{error.includes('invite') && <button onClick={() => logout({ preserveIntent: true })}>{t('authModern.signOutToSwitch')}</button>}<button onClick={() => setRetry(value => value + 1)}>{t('common.retry')}</button></main>;
  if (!ready) return <main className="auth-loading"><p role="status">{t('common.loading')}</p></main>;
  return <PreferencesProvider authenticated={isLoggedIn}><NoteProvider>
    <div className={`App ${!isLoggedIn ? 'app-logged-out' : ''}`}>
      {isCallback ? <AuthCallback config={config} /> : isDebug ? <AuthDebug /> : isFlow && config.enabled ? <Login config={config} /> : isLoggedIn ? (
        needsOnboarding ? <Suspense fallback={<main className="auth-loading"><p role="status">{t('common.loading')}</p></main>}>
          <ProfileOnboarding onComplete={() => setNeedsOnboarding(false)} />
        </Suspense> : <LoggedIn authenticated authConfig={config} />
      ) : config.enabled ? <Login config={config} /> : <LegacyLogin />}
    </div>
  </NoteProvider></PreferencesProvider>;
}
export default App;
